import { useRef, useState } from "react";
import { cn } from "../cn";
import { BRAND_IMAGE_ACCEPT_ATTR, BRAND_IMAGE_MAX_DIMENSION } from "../../lib/brand-image-validate";
import { useBrandImage } from "./useBrandImage";
import { useI18n } from "../../i18n";

/** Longer-edge target (px) for the recommended logo size. */
const RECOMMENDED_LONG_EDGE = 1024;

function gcd(a: number, b: number): number {
  a = Math.abs(Math.round(a));
  b = Math.abs(Math.round(b));
  while (b) [a, b] = [b, a % b];
  return a || 1;
}

/** Human-friendly aspect ratio string for a territory footprint. */
function ratioLabel(w: number, h: number): string {
  const g = gcd(w, h);
  const a = Math.round(w / g);
  const b = Math.round(h / g);
  if (a <= 40 && b <= 40) return `${a}:${b}`;
  return w >= h ? `${(w / h).toFixed(2)}:1` : `1:${(h / w).toFixed(2)}`;
}

/**
 * Recommended logo pixel size that matches the territory footprint so the
 * (fill/stretch) image is NOT distorted. Aspect-matched to the parcel, longer
 * edge = RECOMMENDED_LONG_EDGE, capped at the server max dimension.
 */
function recommendedLogoSize(
  w: number,
  h: number,
): { rw: number; rh: number; ratio: string } | null {
  if (!(w > 0) || !(h > 0)) return null;
  const long = Math.min(RECOMMENDED_LONG_EDGE, BRAND_IMAGE_MAX_DIMENSION);
  let rw: number;
  let rh: number;
  if (w >= h) {
    rw = long;
    rh = Math.max(1, Math.round((long * h) / w));
  } else {
    rh = long;
    rw = Math.max(1, Math.round((long * w) / h));
  }
  return { rw, rh, ratio: ratioLabel(w, h) };
}

/**
 * Dashboard → Territory → Territory Identity → BRAND IMAGE / LOD0.
 *
 * SELECT shows a local preview only (no upload); SAVE persists; REMOVE requires
 * an explicit confirmation. No autosave, no history, no Media Library.
 */
export function BrandImageSection({
  identityId,
  hasAuthority,
  territoryWidth,
  territoryHeight,
}: {
  identityId: string | null;
  hasAuthority: boolean;
  /** Territory bounding-box footprint (world units) for the size hint. */
  territoryWidth?: number;
  territoryHeight?: number;
}) {
  const { t } = useI18n();
  const bi = useBrandImage(identityId, hasAuthority);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const recommended =
    territoryWidth != null && territoryHeight != null
      ? recommendedLogoSize(territoryWidth, territoryHeight)
      : null;

  const onPick = () => fileRef.current?.click();
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void bi.selectFile(f);
    e.target.value = ""; // allow re-selecting the same file
  };

  const busy = bi.status === "saving";
  const shownImage = bi.previewUrl ?? bi.contentUrl;

  return (
    <div
      data-testid="brand-image-section"
      className="mt-6 pt-5 border-t border-border-soft"
    >
      <div className="flex items-center justify-between">
        <h4 className="m-0 font-display text-[12px] font-semibold tracking-[-0.01em] text-text-primary">
          {t("dashboard.brandImage.title")}
        </h4>
        {bi.meta && (
          <span
            data-testid="brand-image-revision"
            className="font-mono text-[9.5px] uppercase tracking-[0.16em] text-text-muted"
          >
            {t("dashboard.brandImage.rev", { n: bi.meta.revision })}
          </span>
        )}
      </div>

      <p className="mt-2 text-[11px] text-text-muted font-mono">
        {t("dashboard.brandImage.desc")}
      </p>

      {recommended && (
        <p
          data-testid="brand-image-recommended"
          className="mt-2 text-[11px] text-neon-teal font-mono leading-relaxed"
        >
          {t("dashboard.brandImage.recommendedPrefix")}{" "}
          <span className="text-text-primary" data-testid="brand-image-recommended-size">
            {t("dashboard.brandImage.size", { rw: recommended.rw, rh: recommended.rh })}
          </span>{" "}
          <span className="text-text-muted">
            {t("dashboard.brandImage.ratioNote", { ratio: recommended.ratio })}
          </span>
        </p>
      )}

      {shownImage && (
        <div className="mt-3 mx-auto rounded-md border border-border-soft bg-obsidian p-2 max-w-[280px]">
          <img
            data-testid="brand-image-preview"
            src={shownImage}
            alt={t("dashboard.brandImage.previewAlt")}
            className="block w-full h-auto max-h-[200px] object-contain"
          />
          {bi.previewUrl && (
            <p data-testid="brand-image-unsaved" className="mt-1 text-[10px] text-neon-amber font-mono">
              {t("dashboard.brandImage.unsaved")}
            </p>
          )}
        </div>
      )}

      {bi.status === "no_image" && !bi.previewUrl && (
        <p data-testid="brand-image-empty" className="mt-3 text-[11px] text-text-muted font-mono">
          {t("dashboard.brandImage.empty")}
        </p>
      )}

      {bi.status === "error" && bi.errorMessage && (
        <p data-testid="brand-image-error" className="mt-3 text-[11px] text-state-danger">
          {bi.errorMessage}
        </p>
      )}
      {bi.status === "success" && (
        <p data-testid="brand-image-saved" className="mt-3 text-[11px] text-neon-teal">
          {t("dashboard.brandImage.saved")}
        </p>
      )}
      {bi.status === "removed" && (
        <p data-testid="brand-image-removed" className="mt-3 text-[11px] text-text-muted">
          {t("dashboard.brandImage.removed")}
        </p>
      )}
      {bi.status === "auth_required" && (
        <p data-testid="brand-image-auth-required" className="mt-3 text-[11px] text-text-muted">
          {t("dashboard.brandImage.authRequired")}
        </p>
      )}

      {bi.status !== "auth_required" && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          <input
            ref={fileRef}
            data-testid="brand-image-file-input"
            type="file"
            accept={BRAND_IMAGE_ACCEPT_ATTR}
            onChange={onFile}
            className="sr-only"
            aria-label={t("dashboard.brandImage.chooseAria")}
          />
          <button
            type="button"
            data-testid="brand-image-select"
            onClick={onPick}
            disabled={busy}
            className={cn(
              "inline-flex items-center h-9 px-5 font-mono text-[11px] tracking-[0.16em] uppercase",
              "rounded-md border border-border-strong bg-transparent text-text-primary hover:bg-white/5",
              "disabled:opacity-50 disabled:cursor-not-allowed",
            )}
          >
            {bi.meta ? t("dashboard.brandImage.replace") : t("dashboard.brandImage.select")}
          </button>

          {bi.previewUrl && (
            <>
              <button
                type="button"
                data-testid="brand-image-save"
                onClick={() => void bi.save()}
                disabled={busy}
                className={cn(
                  "inline-flex items-center h-9 px-5 font-mono text-[11px] font-medium tracking-[0.16em] uppercase",
                  "text-neon-teal bg-transparent border border-border-accent rounded-md hover:bg-neon-teal-soft",
                  "disabled:opacity-50 disabled:cursor-not-allowed",
                )}
              >
                {busy ? t("dashboard.brandImage.saving") : t("dashboard.brandImage.save")}
              </button>
              <button
                type="button"
                data-testid="brand-image-cancel"
                onClick={bi.clearSelection}
                disabled={busy}
                className="inline-flex items-center h-9 px-5 font-mono text-[11px] tracking-[0.16em] uppercase rounded-md border border-border-soft bg-transparent text-text-muted hover:text-text-primary hover:border-border-strong"
              >
                {t("dashboard.brandImage.cancel")}
              </button>
            </>
          )}

          {bi.meta && !bi.previewUrl && !confirmRemove && (
            <button
              type="button"
              data-testid="brand-image-remove"
              onClick={() => setConfirmRemove(true)}
              disabled={busy}
              className="inline-flex items-center h-9 px-5 font-mono text-[11px] tracking-[0.16em] uppercase rounded-md border border-state-danger bg-transparent text-state-danger hover:opacity-80 disabled:opacity-50"
            >
              {t("dashboard.brandImage.remove")}
            </button>
          )}

          {confirmRemove && (
            <>
              <span data-testid="brand-image-remove-confirm" className="w-full text-center text-[11px] text-text-muted font-mono">
                {t("dashboard.brandImage.removeConfirm")}
              </span>
              <button
                type="button"
                data-testid="brand-image-remove-yes"
                onClick={async () => {
                  const ok = await bi.remove();
                  if (ok) setConfirmRemove(false);
                }}
                disabled={busy}
                className="inline-flex items-center h-9 px-5 font-mono text-[11px] font-medium tracking-[0.16em] uppercase text-text-inverse bg-state-danger rounded-md hover:opacity-90 disabled:opacity-50"
              >
                {busy ? t("dashboard.brandImage.removing") : t("dashboard.brandImage.confirm")}
              </button>
              <button
                type="button"
                data-testid="brand-image-remove-no"
                onClick={() => setConfirmRemove(false)}
                disabled={busy}
                className="inline-flex items-center h-9 px-5 font-mono text-[11px] tracking-[0.16em] uppercase rounded-md border border-border-soft bg-transparent text-text-muted hover:text-text-primary hover:border-border-strong"
              >
                {t("dashboard.brandImage.keep")}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
